// Who is on a trip, and who can be put on it. P6-81.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE PICKER IS THE DIRECTORY'S SECOND CONSUMER AND MUST NOT BE A THIRD
//    WAY TO NAME SOMEBODY
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-80 built the map and `compare.js::describePeople` was its first consumer.
// Spec §6 puts the rule plainly: *"Site pages fetch the directory through the
// Function ... build an id→name map once, and join locally."* So this file
// resolves every name through `nameFor` and takes its avatar colour from
// `EntryCard.avatarToken` — the same functions, not new ones. A second
// resolution site is how two screens start disagreeing about what somebody is
// called, and this one would disagree with the compare screen about the very
// people it is inviting.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ONE FLAG, TWO BEHAVIOURS, AND THEY ARE DIFFERENT FUNCTIONS ON PURPOSE
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §4 keeps departed members rendered — *"a row of raw ids in last year's
// MOTY is the wrong answer for a group of friends"* — and they are still on the
// trips they were on. But they cannot sign in, so offering one is offering a
// name nobody can reach and a head the estimate divides by.
//
//     `onTrip`     names everybody on the trip, departed included, marked.
//     `offerable`  omits them.
//
// Two lists rather than one list with a flag, because the screen does two
// different things with them and a single list would make "offered" a property
// the renderer had to remember to check. `functions/_lib/invitee-body.js`
// enforces the same split server-side, which is what makes this one a
// convenience rather than the guard.

import { LEFT_SUFFIX, UNKNOWN_NAME, nameFor } from '../directory.js'
import { avatarToken } from './EntryCard.jsx'

export { UNKNOWN_NAME }

/**
 * One person, resolved for rendering.
 *
 * `label` names the reader as the sentence does — *"Jordan (you)"* — matching
 * `describePeople`'s rule exactly, including its footnote: when we cannot name
 * them, *"you"* is the whole answer, because the placeholder must never appear
 * for the one person the site can always identify.
 */
export function person(id, { directory, you, organiserId } = {}) {
  const { label: name, left, known } = nameFor(directory, id)
  const isYou = you != null && id === you
  const label = known ? (isYou ? `${name} (you)` : name) : isYou ? 'you' : name
  return {
    id,
    token: avatarToken(id),
    label,
    known,
    left,
    isYou,
    isOrganiser: organiserId != null && id === organiserId,
    // Computed here rather than in the component for the reason the whole
    // module exists: the moment a renderer decides how a departed member
    // reads, there are two places that decide it.
    title: left ? `${label} — ${LEFT_SUFFIX}` : label,
  }
}

/**
 * Everybody on the trip, organiser first, then the invitee list in its stored
 * order.
 *
 * 🔴 THE ORDER IS THE SERVICE'S, NOT THIS FILE'S. `access.roster_of` is
 * organiser-first and preserves the stored order for the same reason: a set's
 * iteration order would reshuffle the row between two reads of an unchanged
 * trip. Re-deriving it here as "sorted by name" would make the two disagree the
 * moment somebody's nickname changed.
 */
export function onTrip(trip, context) {
  const organiserId = trip?.organiser_id
  const seen = new Set(organiserId == null ? [] : [organiserId])
  const rows = organiserId == null ? [] : [person(organiserId, context)]
  for (const id of trip?.invitees ?? []) {
    if (seen.has(id)) continue
    seen.add(id)
    rows.push(person(id, context))
  }
  return rows
}

/**
 * Everybody the picker may offer — *"all the server members"*, minus the three
 * groups that are not a choice.
 *
 * ⚠️ IT IS EMPTY WHEN THE DIRECTORY IS EMPTY, AND THAT INCLUDES WHILE IT IS
 * STILL LOADING. There is no fallback to raw ids and there must not be: the
 * whole reason this row waited on P6-80 is that a picker over ids is not a
 * picker. The screen says which of the three states it is in; this returns
 * nothing rather than something wrong.
 */
export function offerable(trip, { directory, you, organiserId } = {}) {
  const already = new Set([trip?.organiser_id, ...(trip?.invitees ?? [])])
  const rows = []
  for (const [id, row] of directory?.byId ?? new Map()) {
    // Departed members are named wherever history renders them and are not a
    // choice here — see the header.
    if (row?.left === true) continue
    if (already.has(id)) continue
    rows.push(person(id, { directory, you, organiserId }))
  }
  // Alphabetical, and the reader first. A list of fifty-three names is scanned,
  // not read, and the one name somebody looks for first is their own — putting
  // it at the top costs nothing and saves the scan.
  rows.sort((a, b) => {
    if (a.isYou !== b.isYou) return a.isYou ? -1 : 1
    return a.label.localeCompare(b.label, 'en')
  })
  return rows
}

/**
 * The delta to send, from the two sets the screen holds.
 *
 * 🔴 A DELTA, NEVER A LIST, and this function is where that decision reaches
 * the browser. The screen knows what the person DID — ticked Chri, unticked
 * Alex — and sending that keeps a concurrent invite from a second session
 * alive, which sending the whole list would silently undo (`trips/routes/
 * trips.py`, and CLAUDE.md gotcha 25 for why the test that proves it needs a
 * warm pool).
 */
export function delta(adding, removing) {
  return { add: [...adding], remove: [...removing] }
}

/** Whether `delta` would ask for anything. The Save button is disabled
 * otherwise, and the service refuses an empty delta anyway — two answers to one
 * question, deliberately, because the button being live and doing nothing is
 * the worse of the two failures. */
export function hasChanges(adding, removing) {
  return adding.size > 0 || removing.size > 0
}

/** How many people the cost is split between, once the pending change lands.
 *
 * The service derives `headcount` and this does NOT second-guess it — `trip
 * .headcount` is the truth for what is saved. This is the PREVIEW, for the
 * sentence beside the Save button, and it is named `pendingHeadcount` so
 * nothing can confuse the two.
 */
export function pendingHeadcount(trip, adding, removing) {
  const roster = new Set([trip?.organiser_id, ...(trip?.invitees ?? [])])
  for (const id of removing) roster.delete(id)
  for (const id of adding) roster.add(id)
  roster.delete(undefined)
  roster.delete(null)
  // The organiser is always on their own trip, so this can never be zero —
  // which matters because it is a denominator.
  if (trip?.organiser_id != null) roster.add(trip.organiser_id)
  return roster.size
}

export const CREW = 'crew'
export const INVITEES = 'invitees'

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE ONE PLACE IN THE BUNDLE THAT DECIDES "CAN ANYBODY ELSE SEE THIS"
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-93 put this fact on two more screens — the board and the trips LIST — and
// the temptation on each of them was to write `trip.headcount === 1`, which is
// what the board's "People · just you" link already says. **That is a headcount
// and not a visibility rule, and using it as one states something false.**
//
// `access.headcount_of` is `len(roster_of(trip))` — organiser plus invitees,
// deduplicated — and its docstring says what it is FOR: it is the denominator
// the cost is split by, and it is never zero. A `crew` trip with no invitees
// has a headcount of one **and is visible to every signed-in member of the
// server**. `src/test/trips/fixtures.js::board()` is exactly that trip, which
// is why the mutant below is worth having.
//
// The rule that decides it lives in `trips/utils/access.py::role_for`:
//
//     organiser        -> ORGANISER
//     in invitees      -> MEMBER
//     visibility crew  -> MEMBER
//     otherwise        -> NONE
//
// so "nobody but the organiser" is `visibility !== 'crew'` AND the roster being
// the organiser alone. Restated here with the smallest possible surface:
//
//   * the roster half is NOT re-derived. `headcount` is the service's own
//     answer, deduplicated by `roster_of` — counting `invitees.length` here
//     would be a second implementation that disagrees the day somebody's own
//     id lands in their own invitee list.
//   * the visibility half is one comparison against a two-valued column, and
//     `people.test.jsx` pins that value set against the migration's CHECK
//     constraint so a third visibility cannot be added without this branch
//     being reconsidered.
//
// ⚠️ AND "YOU" IS LOAD-BEARING. Both sentences below say *you*, which is only
// true if the reader is the organiser. It is, by construction rather than by
// luck: `list_trips` matches on `organiser_id = $1 OR invitees @> [$1] OR
// visibility = 'crew'`, and when this returns true the second and third arms
// cannot have matched. That is asserted rather than trusted — see the test.

/** Can anybody except the organiser open this trip? */
export function onlyYouCanSee(visibility, headcount) {
  if (visibility === CREW) return false
  return !(headcount > 1)
}

/** The trips LIST's marker for that state — the short form of the sentence
 * below, kept beside it so the two cannot drift into saying different things.
 *
 * ⚠️ IT IS A FACT AND NOT A WARNING. Organiser-only is the deliberate default
 * (Jordan, 2026-08-22: *"there are 6 main members but numerous people in the
 * server, so no I do not want it just open"*), so nothing here may read as a
 * mistake the member made — no warn styling, no "you forgot". What was missing
 * was never a different default; it was somebody saying this out loud. */
export const ONLY_YOU = 'Only you can see it'

/**
 * What this trip's visibility means, in words, for the person reading it.
 *
 * 🔴 IT COUNTS PEOPLE, NOT INVITEES, and the difference is the bug this whole
 * row exists to make visible. An `invitees` trip with an empty list is not
 * *"the whole server can see it"* — which is what `src/routes/Trips.jsx`
 * believed and said in a comment — it is a trip **only its organiser can
 * open**. So that state gets its own sentence rather than being folded into the
 * general one, because it is the state every trip started on this site is in
 * until somebody uses this screen.
 */
export function visibilityLine(visibility, headcount) {
  if (visibility === CREW) {
    return 'Anyone in the server can open this trip.'
  }
  if (onlyYouCanSee(visibility, headcount)) {
    return 'Only you can open this trip. Nobody else can see it until you add somebody.'
  }
  return `Only the ${headcount} of you can open this trip.`
}

/** The note under the "on the trip" list, or null.
 *
 * Departed members keep their place and their name — this says so once, rather
 * than the list explaining itself per row. */
export function departedNote(rows) {
  const gone = rows.filter((row) => row.left).length
  if (gone === 0) return null
  return gone === 1
    ? 'One of them has left the server. They stay on the trip until you take them off.'
    : `${gone} of them have left the server. They stay on the trip until you take them off.`
}
